import { c as _c } from "react/compiler-runtime";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useGiveXgSeats } from "@hooks/projects/use-give-xg-seats";
import { areCompanyMembersLoadingSelector, companyMembersSelector, fetchedMembersFlagsSelector } from "@store/members/members-selector";
import { activeSeatsAvailableSelector, isScanPlanSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { assignableTeamsSelector } from "@store/teams/teams-selector";
import { getSelectedOptionValue, getSelectedOptionValues } from "@utils/members-invite-utils";
import { doesProjectRoleNeedXgSeat, getBlockedMessage, getMembersByKey, getSelectionXgSeats, getTeamKeys, getXgSeatChipColor, NO_XG_SEAT_MESSAGE, TEAM_NOT_ALLOWED_MESSAGE } from "@utils/project-invite-xg-seat-utils";
import { useState } from "react";

/** The checkbox that gives the selected members without one a Sphere XG seat along the invite */

/** How giving the seats to the selection went */

/** How the selection fares with the Sphere XG seats that the selected project role needs */

/**
 * Returns the keys that the user agrees to give the people a seat with: their lower-cased emails.
 * Unlike an identity, the email of somebody outside the workspace stays the same once they join it,
 * so the consent holds when they need their seat again, e.g. when the invite is retried.
 */
function getSeatKeys({
  membersToSeat,
  emailsToSeat
}) {
  return [...membersToSeat.map(({
    email
  }) => email), ...emailsToSeat].map(email => email.toLowerCase());
}

/** Giving no seats */
const NO_FAILURES = {
  failures: [],
  numberOfSeatsGiven: 0,
  failedValues: new Set()
};

/** A selection that needs no seats, because of the plan or the project role */
const SELECTION_WITHOUT_SEATS = {
  getChipColor: () => undefined,
  isValid: true,
  giveSeats: () => Promise.resolve(NO_FAILURES)
};

/**
 * Applies the Sphere XG seats to inviting members to a project. On a Scan plan, only members that
 * hold a Sphere XG seat can be given a project role other than viewer, and teams cannot be given
 * one at all.
 *
 * Users that manage the seats may give one to the selected members without, as long as the
 * workspace has enough free seats. Everybody else, and they too once no seat is left, can only
 * select the members holding one.
 */
export function useProjectInviteXgSeats(role, location) {
  const $ = _c(24);
  const {
    giveSeats
  } = useGiveXgSeats(location);
  const {
    canManageXgSeats
  } = useHasUserValidRoleCompanyLevel();
  const isScanPlan = useAppSelector(isScanPlanSelector);
  const companyMembers = useAppSelector(companyMembersSelector);
  const teams = useAppSelector(assignableTeamsSelector);
  const seatsAvailable = useAppSelector(activeSeatsAvailableSelector);
  const areMembersLoading = useAppSelector(areCompanyMembersLoadingSelector);
  const {
    hasFetchedWorkspaceMembers
  } = useAppSelector(fetchedMembersFlagsSelector);
  const [consentedKeys, setConsentedKeys] = useState(_temp);
  let t0;
  if ($[0] !== isScanPlan || $[1] !== role) {
    t0 = isScanPlan && doesProjectRoleNeedXgSeat(role);
    $[0] = isScanPlan;
    $[1] = role;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const isSeatRequired = t0;
  const isOutOfSeats = seatsAvailable === 0;
  const canGiveSeats = canManageXgSeats && !isOutOfSeats;
  let t1;
  if ($[3] !== companyMembers) {
    t1 = getMembersByKey(companyMembers);
    $[3] = companyMembers;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const membersByKey = t1;
  let t2;
  if ($[5] !== teams) {
    t2 = getTeamKeys(teams);
    $[5] = teams;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const teamKeys = t2;
  let checkSelection;
  if ($[7] !== areMembersLoading || $[8] !== canGiveSeats || $[9] !== canManageXgSeats || $[10] !== consentedKeys || $[11] !== giveSeats || $[12] !== hasFetchedWorkspaceMembers || $[13] !== isOutOfSeats || $[14] !== isSeatRequired || $[15] !== membersByKey || $[16] !== seatsAvailable || $[17] !== teamKeys) {
    checkSelection = function checkSelection(selectedOptions) {
      if (!isSeatRequired) {
        return SELECTION_WITHOUT_SEATS;
      }
      const selectedValues = getSelectedOptionValues(selectedOptions);
      const selection = getSelectionXgSeats({
        selectedValues,
        membersByKey,
        teamKeys,
        areAllMembersKnown: hasFetchedWorkspaceMembers,
        canGiveSeats
      });
      const {
        blockedValues,
        membersToSeat,
        emailsToSeat
      } = selection;
      const seatKeys = getSeatKeys(selection);
      const hasEnoughSeats = hasSeatsFor(seatKeys.length);
      const areSeatsGiven = isConsentGiven(seatKeys) && hasEnoughSeats;
      return {
        getChipColor: option => getXgSeatChipColor(getSelectedOptionValue(option), selection, hasEnoughSeats),
        helperText: getBlockedHelperText(blockedValues),
        isValid: !areMembersLoading && !blockedValues.size && (!seatKeys.length || areSeatsGiven),
        giveSeatsCheckbox: getGiveSeatsCheckbox(seatKeys, areSeatsGiven),
        giveSeats: async () => {
          if (!areSeatsGiven) {
            return NO_FAILURES;
          }
          const givenSeats = await giveSeats({
            members: membersToSeat,
            emails: emailsToSeat
          });
          const failedKeys = new Set(givenSeats.failures.map(_temp2));
          return {
            ...givenSeats,
            failedValues: new Set(selectedValues.filter(value => failedKeys.has(getSeatKey(value))))
          };
        }
      };
    };
    function getSeatKey(value_0) {
      const member = membersByKey.get(value_0) ?? membersByKey.get(value_0.toLowerCase());
      return member?.identity ?? value_0.toLowerCase();
    }
    function isConsentGiven(keys) {
      return !!keys.length && keys.every(key_0 => consentedKeys.has(key_0));
    }
    function hasSeatsFor(numberOfPeople) {
      return seatsAvailable === undefined || numberOfPeople <= seatsAvailable;
    }
    function getBlockedHelperText(blockedValues_0) {
      if (!blockedValues_0.size) {
        return;
      }
      const text = getBlockedMessage({
        reasons: new Set(blockedValues_0.values()),
        isOutOfSeats: canManageXgSeats && isOutOfSeats
      });
      return {
        type: "error",
        text
      };
    }
    function getGiveSeatsCheckbox(keys_0, areSeatsGiven_0) {
      if (!keys_0.length) {
        return;
      }
      return {
        numberOfMembers: keys_0.length,
        seatsAvailable,
        isChecked: areSeatsGiven_0,
        onChange: isNowChecked => setConsentedKeys(new Set(isNowChecked ? keys_0 : []))
      };
    }
    $[7] = areMembersLoading;
    $[8] = canGiveSeats;
    $[9] = canManageXgSeats;
    $[10] = consentedKeys;
    $[11] = giveSeats;
    $[12] = hasFetchedWorkspaceMembers;
    $[13] = isOutOfSeats;
    $[14] = isSeatRequired;
    $[15] = membersByKey;
    $[16] = seatsAvailable;
    $[17] = teamKeys;
    $[18] = checkSelection;
  } else {
    checkSelection = $[18];
  }
  const t3 = isSeatRequired && !canGiveSeats ? NO_XG_SEAT_MESSAGE : undefined;
  const t4 = isSeatRequired ? TEAM_NOT_ALLOWED_MESSAGE : undefined;
  let t5;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setConsentedKeys(new Set());
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== checkSelection || $[21] !== t3 || $[22] !== t4) {
    t6 = {
      memberWithoutSeatDisabledMessage: t3,
      teamDisabledMessage: t4,
      checkSelection,
      reset: t5
    };
    $[20] = checkSelection;
    $[21] = t3;
    $[22] = t4;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}
function _temp2(t0) {
  const {
    key
  } = t0;
  return key;
}
function _temp() {
  return new Set();
}